import type { FC } from "react";
import React, { useMemo } from "react";

import cn from "classnames";

import ButtonCellActionLeftAddon from "./components/LeftAddon/ButtonCellActionLeftAddon";
import ButtonCellActionRightAddon from "./components/RightAddon/ButtonCellActionRightAddon";
import { BUTTON_CELL_ACTION_DISPLAY_NAME } from "./utils/core";
import { createTestIdForButtonCellActionElement } from "./utils/getTestId";
import { ButtonCell } from "..";
import { extractProps, getChildrenRecord } from "../../../utils/react";

import type { ButtonCellActionProps } from "./types";

import classes from "./ButtonCellAction.styl";

export const BUTTON_CELL_ACTION_TEST_ID =
    createTestIdForButtonCellActionElement("button-cell-action");

/**
 * Компонент кнопки, который отображает доступное действие в виде горизонтальной ячейки.
 *
 * Основан на компоненте ButtonCell и переиспользует динамические отступы, область нажатия и все состояния.
 *
 * По умолчанию отображает contentsView и leftIcon, можно проставлять selected - будет отображаться rightIcon.
 * Может принимать в себя любые компоненты в bottomAddon, дополнительные слоты для удобного формирования наследников.
 *
 * Также можно настраивать стили для label и leftIcon.
 *
 * <a href="https://www.figma.com/design/64y1aDfl0lSSpek3sq1Lrt/%F0%9F%8D%AC-Cards-%26-Cells-%26-Views?node-id=4220-52506&m=dev" target="_blank">Спека</a>
 * @constructor
 */
export const ButtonCellAction: FC<ButtonCellActionProps> = (props) => {
    const {
        customVarsStyles,
        currentProps: {
            state = "enabled",
            children,
            view = "primary",
            selected = false,
            prefix = BUTTON_CELL_ACTION_DISPLAY_NAME,
            "data-tsid": tsid = BUTTON_CELL_ACTION_TEST_ID,
            ...restProps
        },
    } = useMemo(() => extractProps(props, ["customVarsStyles"]), [props]);

    const { Label, Subtitle, Value, LeftIcon, BottomAddon } = useMemo(
        () =>
            getChildrenRecord(children, prefix, {
                Label: {
                    required: true,
                    props: {
                        parentView: view,
                    },
                },
                Unknown: {
                    skipElement: true,
                },
            }),
        [children, prefix, view],
    );

    const buttonCellActionStyles = cn(
        classes["button-cell-action"],
        view !== "secondary" && classes[`__view-${view}`],
        customVarsStyles,
    );

    return (
        <ButtonCell
            {...restProps}
            state={state}
            customVarsStyles={buttonCellActionStyles}
            data-tsid={tsid}
            prefix={prefix}
            data-uikit={prefix}
            aria-pressed={selected || undefined}
        >
            {Label}

            {Subtitle}

            {Value}

            {LeftIcon && (
                <ButtonCellActionLeftAddon>
                    {LeftIcon}
                </ButtonCellActionLeftAddon>
            )}

            {selected && <ButtonCellActionRightAddon />}

            {BottomAddon}
        </ButtonCell>
    );
};

ButtonCellAction.displayName = BUTTON_CELL_ACTION_DISPLAY_NAME;

export default ButtonCellAction;
